import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { de, type Strings } from '@/i18n/de'
import { en } from '@/i18n/en'

export type Lang = 'de' | 'en'

const STORAGE_KEY = 'htp-lang'
const catalogs: Record<Lang, Strings> = { de, en: en as unknown as Strings }

type I18nCtx = {
  lang: Lang
  t: Strings
  setLang: (lang: Lang) => void
  switchLang: (lang: Lang) => void
  langPath: (path?: string) => string
  stripLang: (pathname: string) => string
}

const Ctx = createContext<I18nCtx | null>(null)

export function readStoredLang(): Lang | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'de' || v === 'en') return v
  } catch {
    /* ignore */
  }
  return null
}

export function persistLang(lang: Lang) {
  try {
    localStorage.setItem(STORAGE_KEY, lang)
  } catch {
    /* ignore */
  }
}

/** Extract language prefix from pathname (/de/... or /en/...). */
export function langFromPath(pathname: string): Lang | null {
  const m = pathname.match(/^\/(de|en)(?=\/|$)/)
  return m ? (m[1] as Lang) : null
}

/** Path without /de or /en prefix (keeps leading slash on remainder, default '/'). */
export function stripLangPrefix(pathname: string): string {
  const stripped = pathname.replace(/^\/(de|en)(?=\/|$)/, '') || '/'
  return stripped.startsWith('/') ? stripped : `/${stripped}`
}

/** Build a path under the given (or current) language. */
export function buildLangPath(lang: Lang, path = '/'): string {
  let rest = path.startsWith('/') ? path : `/${path}`
  rest = stripLangPrefix(rest)
  if (rest === '/') return `/${lang}/`
  return `/${lang}${rest}`
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const lang = langFromPath(location.pathname) || readStoredLang() || 'de'

  useEffect(() => {
    persistLang(lang)
    document.documentElement.lang = lang
  }, [lang])

  // Honor stored preference when landing on bare / or mismatched default
  useEffect(() => {
    const stored = readStoredLang()
    const inPath = langFromPath(location.pathname)
    if (!inPath && stored) {
      navigate(buildLangPath(stored, location.pathname + location.search), { replace: true })
    }
  }, [location.pathname, location.search, navigate])

  const setLang = useCallback((next: Lang) => {
    persistLang(next)
  }, [])

  const switchLang = useCallback(
    (next: Lang) => {
      if (next === lang) return
      persistLang(next)
      const rest = stripLangPrefix(location.pathname)
      navigate(buildLangPath(next, rest + location.search), { replace: false })
    },
    [lang, location.pathname, location.search, navigate],
  )

  const langPath = useCallback((path = '/') => buildLangPath(lang, path), [lang])

  const value = useMemo<I18nCtx>(
    () => ({
      lang,
      t: catalogs[lang],
      setLang,
      switchLang,
      langPath,
      stripLang: stripLangPrefix,
    }),
    [lang, setLang, switchLang, langPath],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useI18n(): I18nCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useI18n must be used within LanguageProvider')
  return ctx
}

/** Safe strings accessor for non-React modules — prefers document lang / storage / de. */
export function getStrings(): Strings {
  if (typeof document !== 'undefined') {
    const htmlLang = document.documentElement.lang
    if (htmlLang === 'en' || htmlLang === 'de') return catalogs[htmlLang]
  }
  const stored = typeof localStorage !== 'undefined' ? readStoredLang() : null
  return catalogs[stored || 'de']
}

export function getLang(): Lang {
  if (typeof document !== 'undefined') {
    const htmlLang = document.documentElement.lang
    if (htmlLang === 'en' || htmlLang === 'de') return htmlLang
  }
  return readStoredLang() || 'de'
}
